import React, { Key, useState } from "react";
import style from "./index.module.scss";
import { Button, Dropdown, Icon, Menu, message, Modal } from "antd";
import useModalHandler from "utils/useModalHandler";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage } from "service/service";
import UploadFileModal from "../UploadFileModal";
import ResultModal from "../ResultModal";
import UploadModal from "components/UploadModal";
import { useAtomValue } from "jotai";
import { batchNoAtom } from "service/stores/CommonStore";
import {
    deleteMyQuestions,
    findImportLog,
    importQuestionExcel
} from "service/answerStore";
import useRequest from "utils/useRequest";
import useRequestFn from "../../../../../utils/useRequestFn";

interface ToolsProps {
    selectedRowKeys: Key[]; //勾选选中项
    updateData: () => void; //更新列表数据
    exportData(): Promise<any>; // 导出题库
    restoreSelect: (val: string[]) => void; //重置勾选项
}

export default function Tools({
    selectedRowKeys,
    updateData,
    exportData,
    restoreSelect
}: ToolsProps) {
    const uploadModal = useModalHandler();
    const resultModal = useModalHandler();

    const currentBatchNo = useAtomValue(batchNoAtom); //当前学期编号
    const [batchNo, setBatchNo] = useState(currentBatchNo || ""); //学期编号

    const [logList, { loading: logLoading }, updateLog] =
        useRequest(findImportLog);

    const [onExport, exportStatus] = useRequestFn(async () => {
        try {
            await exportData();
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    //导入文件
    const onUploadOk = async (file?: File) => {
        if (!file) return;
        onClose();

        try {
            const data = await importQuestionExcel(file, batchNo);
            const {
                data: { message: msg }
            } = data;
            msg.indexOf("完毕") !== -1
                ? message.success(msg)
                : ResultMessage.error(msg, {
                      duration: 2000,
                      title: "试题导入失败",
                      icon: "exclamation-circle",
                      width: 350
                  });
            updateData();
            updateLog();
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    };
    const onClose = () => {
        uploadModal.close();
        setBatchNo(currentBatchNo!);
    };

    //批量删除函数
    function handleDelete() {
        if (selectedRowKeys.length === 0) {
            Modal.confirm({
                title: `尚未选择要移除的记录，请勾选要删除的数据后点击”批量删除“按钮。`
            });
        } else {
            Modal.confirm({
                title: `批量删除题目？`,
                content: "被勾选的题目删除后将无法恢复，确定删除？",
                onOk: async () => {
                    try {
                        await deleteMyQuestions(selectedRowKeys);
                        message.success("删除成功");
                        restoreSelect([]);
                        updateData();
                    } catch (e) {
                        ResultMessage.error(errorMessage(e));
                    }
                }
            });
        }
    }

    const tips = (
        <div className={style.tip}>
            使用说明：
            <br />
            1.仅支持导入的题型：单选题、多选题、填空题、判断题，不支持导入一个大标题下有多个小问题的题目。
            <br />
            2.不支持导入图片/音视频/公式。
        </div>
    );

    const menu = (
        <Menu>
            <Menu.Item
                key="1"
                className={style.menuItem}
                onClick={handleDelete}>
                批量删除
            </Menu.Item>
        </Menu>
    );

    return (
        <div className={style.root}>
            <Button
                className={style.btn}
                onClick={onExport}
                loading={exportStatus.loading}>
                导出题库
            </Button>

            <Button className={style.btn} onClick={uploadModal.open}>
                Excel导入题目
            </Button>

            <Dropdown overlay={menu} className={style.btn} trigger={["click"]}>
                <Button>
                    {" "}
                    批量操作
                    <Icon type="down" />
                </Button>
            </Dropdown>

            <Button type="link" onClick={resultModal.open}>
                查看导入结果
            </Button>

            {uploadModal.render(
                <UploadModal
                    title="Excel批量导入试题"
                    onCancel={onClose}
                    onUploadOk={onUploadOk}
                    visible={uploadModal.visible}
                    tips={tips}>
                    <UploadFileModal
                        batchNo={batchNo}
                        onchangeBatchNo={setBatchNo}
                    />
                </UploadModal>
            )}

            {resultModal.render(
                <ResultModal
                    data={logList || []}
                    loading={logLoading}
                    visible={resultModal.visible}
                    onCancel={resultModal.close}
                />
            )}
        </div>
    );
}
